import { all, call, put, select, takeLatest, delay } from 'redux-saga/effects'
import { api } from 'network/api'
import constants from 'utils/constants'
import { getCurrentCountry, getJWTDetails } from 'utils/appUtils'
import { language } from 'context/LanguageContext/strings'
import { getHomePageItems } from '../products/actions'
import { getOrderUrl, getOrderListUrl, getOrderDeliveryUrl, submitFeedbackUrl } from './urls'
import {
    actions,
    getCurrentOrdersList,
    getCurrentOrdersListSuccess,
    getCurrentOrdersListFailed,
    getPastOrdersList,
    getPastOrdersListSuccess,
    getPastOrdersListFailed,
    getOrderDeliverySuccess,
    getOrderDeliveryFailed,
    getOrderStatusSuccess,
    getOrderStatusFailed,
    submitFeedbackSuccess,
    submitFeedbackFailed,
    getLastPastOrdersListSuccess,
    getLastPastOrdersListFailed,
    getLastCurrentOrdersListSuccess,
    getLastCurrentOrdersListFailed,
    getOrderDetailsFailed,
    getOrderDetailsSuccess,
} from './actions'

const DELAY_REFRESH = 10000

//= ============================================================================================================

function* getCurrentOrderList(statuses) {
    try {
        const { location, user } = yield select()
        const { isSignedIn } = getJWTDetails()
        const userCountry = getCurrentCountry(location, user)
        if (isSignedIn) {
            return yield call(
                api,
                getOrderListUrl,
                'POST',
                {
                    order_status: statuses,
                    country: userCountry,
                },
                0,
                null,
                false,
                language
            )
        }
    } catch (error) {
        yield put(getCurrentOrdersListFailed(error))
        console.log('Error from getCurrentOrderList', error)
    }
    return
}

function* getPastOrderList(statuses, lastIndex, next) {
    try {
        const { location, user } = yield select()
        const userCountry = getCurrentCountry(location, user)
        const { isSignedIn } = getJWTDetails()
        if (isSignedIn) {
            return yield call(
                api,
                getOrderListUrl,
                'POST',
                {
                    order_status: statuses,
                    limit: constants.fetchOrdersLimit,
                    lastKey: next ? lastIndex : 0,
                    country: userCountry,
                },
                0,
                null,
                false,
                language
            )
        }
    } catch (error) {
        console.log('Error from getPastOrderList', error)
        yield put(getCurrentOrdersListFailed(error))
    }
}

//= ============================================================================================================

function* getCurrentOrdersListWatch() {
    try {
        const ordersResult = yield* getCurrentOrderList(constants.in_progress_statuses)
        if (ordersResult) {
            yield put(getCurrentOrdersListSuccess(ordersResult))
        } else yield put(getCurrentOrdersListFailed())
    } catch (error) {
        yield put(getCurrentOrdersListFailed(error))
    }
}

//= ============================================================================================================

function* getPastOrdersListWatch({ payload: next }) {
    try {
        const state = yield select()
        const { lastIndex } = state.orders.get('pastOrders')
        const ordersResult = yield* getPastOrderList(constants.final_statuses, lastIndex, next)
        if (ordersResult) {
            yield put(getPastOrdersListSuccess({ ...ordersResult, next }))
        } else yield put(getPastOrdersListFailed())
    } catch (error) {
        yield put(getPastOrdersListFailed(error))
    }
}

function* getLastCurrentOrdersListWatch({ payload: next }) {
    try {
        const state = yield select()
        const order_history_limit = state.configuration.get('order_history_limit')
        const { lastIndex } = state.orders.get('lastCurrentOrders')
        const { location, user } = yield select()
        const userCountry = getCurrentCountry(location, user)
        const ordersResult = yield call(
            api,
            getOrderListUrl,
            'POST',
            {
                order_status: constants.in_progress_statuses,
                OrderHistoryLimit: order_history_limit,
                limit: constants.fetchOrdersLimit,
                lastKey: next ? lastIndex : 0,
                country: userCountry,
            },
            0,
            null,
            false,
            language
        )

        yield put(getLastCurrentOrdersListSuccess({ ...ordersResult, next }))
    } catch (error) {
        console.log('Error from getLastCurrentOrdersListWatch', error)
        yield put(getLastCurrentOrdersListFailed(error))
    }
}
function* getLastPastOrdersListWatch({ payload: next }) {
    try {
        const state = yield select()
        const order_history_limit = state.configuration.get('order_history_limit')
        const { lastIndex } = state.orders.get('lastPastOrders')
        const { location, user } = yield select()
        const userCountry = getCurrentCountry(location, user)

        const ordersResult = yield call(
            api,
            getOrderListUrl,
            'POST',
            {
                order_status: constants.final_statuses,
                OrderHistoryLimit: order_history_limit,
                limit: constants.fetchOrdersLimit,
                lastKey: next ? lastIndex : 0,
                country: userCountry,
            },
            0,
            null,
            false,
            language
        )

        yield put(getLastPastOrdersListSuccess({ ...ordersResult, next }))
    } catch (error) {
        console.log('Error from getLastPastOrdersListWatch', error)
        yield put(getLastPastOrdersListFailed(error))
    }
}
//= ============================================================================================================

function* startPollingForOrderStatus() {
    while (true) {
        try {
            const state = yield select()
            const currentOrder = state.orders.get('currentOrder')

            if (!currentOrder || !currentOrder.polling) {
                // console.log('Polling Cancelled')
                return
            }

            const orderId = currentOrder.id
            if (orderId) {
                // refresh status & delivery
                yield* getOrderStatus({ payload: orderId })
            }
        } catch (error) {
            console.log('pollingOrderStatus:err!', error)
        } finally {
            yield delay(DELAY_REFRESH)
        }
    }
}

function* getOrderStatus(action) {
    const { payload: orderId } = action
    let response = null

    const timeStamp = new Date().getTime()
    try {
        response = yield call(api, `${getOrderUrl}/${orderId}?timeStamp=${timeStamp}`, 'GET', null)
        yield put(getOrderStatusSuccess(response))
    } catch (error) {
        yield put(getOrderStatusFailed(error))
        return // Exit early on error to prevent accessing null response
    }

    // should poll for delivery tracking?
    const history = (response && response.order_history) || []
    if (
        history &&
        history.find(oh => oh.order_status === 'ready' || oh.order_status === 'out_for_delivery') &&
        response?.order_type?.toLowerCase() === 'delivery'
    ) {
        yield getOrderDelivery(orderId)
    }

    // reached order final status? refresh orders lists
    const lastEvent = history[history.length - 1] || {}
    if (constants.final_statuses.includes(lastEvent.order_status)) {
        yield put(getCurrentOrdersList())
        yield put(getPastOrdersList())
    }
}
function* getOrderDetails(action) {
    const { payload: orderId } = action
    let response = null
    try {
        response = yield call(api, `${getOrderUrl}/${orderId}`, 'GET', null)
        yield put(getOrderDetailsSuccess(response))
    } catch (error) {
        yield put(getOrderDetailsFailed(error))
    }
}

function* getOrderDelivery(orderId) {
    try {
        const params = {
            delivery_id: true,
            dropoff_eta: true,
            courier_info: true,
            history: false,
            location: true,
            'data.pickup': true,
            'data.dropoff': true,
            'data.dropoff_eta': true,
        }

        const response = yield call(api, `${getOrderDeliveryUrl}/${orderId}`, 'POST', params)

        if (
            response &&
            response.data &&
            response.data.dropoff &&
            response.data.pickup &&
            response.courier_info
        ) {
            const courierInfo = {
                courierName: response.courier_info.courier_name,
                courierProvider: response.courier_info.provider,
                courierPhone: response.courier_info.courier_phone,
                courierImage: response.courier_info.courier_image,
                currentLocation: response.location,
                pickupLocation: response.data.pickup.location,
                dropoffLocation: response.data.dropoff.location,
                dropOffETA: response.data.dropoff_eta,
            }
            yield put(getOrderDeliverySuccess(courierInfo))
        } else {
            // console.log('Order Delivery', response)
        }
    } catch (error) {
        console.log('updateOrderDelivery:err!', error)
        yield put(getOrderDeliveryFailed(error))
    }
}

//= ============================================================================================================

function* submitFeedback(action) {
    const { rating, orderId, comment } = action.payload
    try {
        const params = {
            customer_feedback: rating
                ? {
                      rating,
                      comment,
                  }
                : {},
        }

        const response = yield call(api, `${submitFeedbackUrl}/${orderId}`, 'POST', params)

        yield put(submitFeedbackSuccess(response))
    } catch (error) {
        yield put(submitFeedbackFailed(error))
    }
    yield put(getHomePageItems())
}

// function* reorder({ payload: order }) {
//     if (order === null || order === undefined || (order && order.length === 0)) {
//         return
//     }
//     if (
//         order.items === null ||
//         order.items === undefined ||
//         (order.items && order.items.length === 0)
//     ) {
//         return
//     }
//     for (let i = 0; i < order.items.length; i++) {
//         const pastOrderItem = order.items[i]
//         pastOrderItem.product_oos_option = pastOrderItem.product_oos_option || 'REMOVE'
//         for (let j = 0; j < pastOrderItem.quantity; j++) {
//             yield put(addItemToBasket(pastOrderItem))
//         }
//     }
//
//     Push('orders', 'BasketItems', { root: 'orders' }, true, true)
//
//     // For analytic purposes.
//     yield put(setIsReorder(true))
// }

//= ============================================================================================================
function* getOrderStatusWatcher() {
    yield takeLatest(actions.GET_ORDER_STATUS, getOrderStatus)
}

function* getCurrentOrdersListWatcher() {
    yield takeLatest(actions.GET_CURRENT_ORDERS_LIST, getCurrentOrdersListWatch)
}

function* getPastOrdersListWatcher() {
    yield takeLatest(actions.GET_PAST_ORDERS_LIST, getPastOrdersListWatch)
}

function* getStartPollingOrderStatusWatcher() {
    yield takeLatest(actions.START_POLLING_ORDER_STATUS, startPollingForOrderStatus)
}

function* submitFeedbackWatcher() {
    yield takeLatest(actions.SUBMIT_FEEDBACK, submitFeedback)
}

function* getLastPastOrdersListWatcher() {
    yield takeLatest(actions.GET_LAST_PAST_ORDERS_LIST, getLastPastOrdersListWatch)
}

function* getLastCurrentOrdersListWatcher() {
    yield takeLatest(actions.GET_LAST_CURRENT_ORDERS_LIST, getLastCurrentOrdersListWatch)
}

function* getOrderDetailsWatcher() {
    yield takeLatest(actions.GET_ORDER_DETAILS, getOrderDetails)
}
// function* requestReorderWatcher() {
//    yield takeLatest(actions.REORDER, reorder)
// }

export default function* orderSaga() {
    yield all([
        getCurrentOrdersListWatcher(),
        getPastOrdersListWatcher(),
        getStartPollingOrderStatusWatcher(),
        submitFeedbackWatcher(),
        // requestReorderWatcher(),
        getOrderStatusWatcher(),
        getOrderDetailsWatcher(),
        getLastPastOrdersListWatcher(),
        getLastCurrentOrdersListWatcher(),
    ])
}
